import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Award,
  Users,
  Target,
  TrendingUp,
  BookOpen,
  Shield,
  Calendar,
  Wrench,
  BarChart3,
  Ruler,
  FileCheck,
  GitBranch,
  Zap
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { UpcomingTrainingsSection } from '../components/UpcomingTrainingsSection';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

export const CoreToolsTraining = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Core Tools Training | APQP, PPAP, FMEA, MSA & SPC Certification",
    "description": "Get industry-ready Core Tools Training on APQP, PPAP, FMEA, MSA & SPC. Ideal for automotive and manufacturing quality professionals.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy"
    },
    "coursePrerequisites": "Basic quality management knowledge recommended",
    "educationalLevel": "Intermediate to Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "In-person",
      "duration": "3-5 days"
    }
  };

  const coreTools = [
    {
      id: 'apqp',
      title: 'APQP',
      fullName: 'Advanced Product Quality Planning',
      icon: GitBranch,
      color: 'indigo',
      gradient: 'from-indigo-500 to-blue-600',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      borderColor: 'border-indigo-200',
      description: 'A structured framework for developing new products and processes that ensures customer requirements are met from concept through production.',
      keyPoints: [
        'Product quality planning roadmap',
        'Cross-functional team approach',
        'Risk identification and mitigation',
        'Design and process validation',
        'Customer satisfaction focus'
      ]
    },
    {
      id: 'ppap',
      title: 'PPAP',
      fullName: 'Production Part Approval Process',
      icon: FileCheck,
      color: 'green',
      gradient: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-200',
      description: 'A standardized process to demonstrate that suppliers can consistently meet customer engineering design and specification requirements.',
      keyPoints: [
        'Part submission warrant (PSW)',
        'Design documentation verification',
        'Process capability evidence',
        'Dimensional measurement results',
        'Customer approval documentation'
      ]
    },
    {
      id: 'fmea',
      title: 'FMEA',
      fullName: 'Failure Mode and Effects Analysis',
      icon: Shield,
      color: 'red',
      gradient: 'from-red-500 to-orange-600',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
      borderColor: 'border-red-200',
      description: 'A systematic method for identifying potential failures in design or processes and implementing actions to prevent them before they occur.',
      keyPoints: [
        'Design FMEA (DFMEA)',
        'Process FMEA (PFMEA)',
        'Risk priority number (RPN)',
        'Severity, Occurrence, Detection ratings',
        'Corrective action tracking'
      ]
    },
    {
      id: 'msa',
      title: 'MSA',
      fullName: 'Measurement System Analysis',
      icon: Ruler,
      color: 'purple',
      gradient: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      description: 'A methodology to assess the quality of measurement systems and ensure that data used for decision-making is reliable and accurate.',
      keyPoints: [
        'Gage Repeatability & Reproducibility (GR&R)',
        'Measurement system variability',
        'Bias, linearity, and stability studies',
        'Attribute agreement analysis',
        'Measurement uncertainty assessment'
      ]
    },
    {
      id: 'spc',
      title: 'SPC',
      fullName: 'Statistical Process Control',
      icon: BarChart3,
      color: 'cyan',
      gradient: 'from-cyan-500 to-blue-600',
      bgColor: 'bg-cyan-50',
      textColor: 'text-cyan-600',
      borderColor: 'border-cyan-200',
      description: 'A statistical method to monitor and control processes, ensuring they operate at their full potential and produce conforming products.',
      keyPoints: [
        'Control charts (X-bar, R, P, C)',
        'Process capability indices (Cp, Cpk)',
        'Common vs. special cause variation',
        'Process stability monitoring',
        'Continuous improvement through data'
      ]
    }
  ];

  const criticalReasons = [
    {
      icon: Shield,
      title: 'Compliance with IATF 16949 & ISO Standards',
      description: 'Core Tools Training is mandatory for organizations certified to IATF 16949 and highly recommended for ISO 9001, ISO 13485, and AS9100 environments.'
    },
    {
      icon: Target,
      title: 'Proactive Risk Management',
      description: 'Core tools focus on preventing defects rather than detecting them after occurrence, saving time and cost.'
    },
    {
      icon: TrendingUp,
      title: 'Improved Product & Process Quality',
      description: 'Organizations experience reduced variation, higher consistency, and better reliability.'
    },
    {
      icon: Users,
      title: 'Customer Satisfaction & Trust',
      description: 'Demonstrates commitment to quality, compliance, and continuous improvement.'
    },
    {
      icon: Zap,
      title: 'Reduced Cost of Poor Quality (COPQ)',
      description: 'Minimizes scrap, rework, warranty claims, and recalls.'
    }
  ];

  const whoShouldAttend = [
    'Quality Engineers and Managers',
    'Product Development Engineers',
    'Manufacturing and Process Engineers',
    'Supplier Quality Development professionals',
    'IATF 16949 and ISO auditors',
    'Operations and Production Managers',
    'Design and R&D teams',
    'Anyone involved in automotive or manufacturing quality'
  ];

  const trainingBenefits = [
    {
      icon: BookOpen,
      title: 'Comprehensive Knowledge',
      description: 'Gain in-depth understanding of all five core tools and their practical applications in real-world scenarios.'
    },
    {
      icon: Award,
      title: 'Industry Recognition',
      description: 'Receive certification recognized by automotive OEMs, tier suppliers, and quality-driven industries worldwide.'
    },
    {
      icon: Wrench,
      title: 'Hands-On Practice',
      description: 'Participate in practical exercises, case studies, and workshops using actual industry examples and templates.'
    },
    {
      icon: TrendingUp,
      title: 'Career Advancement',
      description: 'Enhance your professional credentials and open doors to quality management and engineering roles.'
    },
    {
      icon: Users,
      title: 'Expert Instruction',
      description: 'Learn from experienced trainers with extensive automotive and manufacturing industry backgrounds.'
    },
    {
      icon: CheckCircle,
      title: 'Implementation Support',
      description: 'Get practical guidance on implementing core tools in your organization for immediate results.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Core Tools Training | APQP, PPAP, FMEA, MSA & SPC Certification"
        description="Get industry-ready Core Tools Training on APQP, PPAP, FMEA, MSA & SPC. Ideal for automotive and manufacturing quality professionals."
        keywords="core tools training, APQP, PPAP, FMEA, MSA, SPC, IATF 16949, automotive quality, quality core tools, AIAG training, manufacturing quality"
        canonical="/Core-Tools-Training"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'Core Tools Training', path: '/Core-Tools-Training' }
        ]}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-slate-700 via-gray-800 to-slate-900 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              <Wrench className="h-5 w-5" />
              AUTOMOTIVE & MANUFACTURING QUALITY
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Core Tools Training - Overview
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto">
              Master APQP, PPAP, FMEA, MSA & SPC - the essential quality tools for automotive and manufacturing excellence. Industry-recognized certification for quality professionals.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>3-5 Days Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-6 w-6" />
                <span>Practical Workshops</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Core Tools Training is an essential requirement for organizations involved in manufacturing, automotive, aerospace, medical devices, and other quality-driven industries. These tools form the backbone of quality management systems and are widely mandated by IATF 16949, ISO 9001, and customer-specific requirements.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Core Tools Training equips professionals with structured methodologies to prevent defects, reduce risk, ensure compliance, and improve customer satisfaction. By mastering these tools, organizations can achieve consistent quality, reduce rework and recalls, and enhance operational efficiency.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Are Quality Core Tools Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Are Quality Core Tools?
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              The Quality Core Tools are a set of standardized methods developed by the Automotive Industry Action Group (AIAG) and globally recognized across industries. These tools focus on risk prevention, process control, and continuous improvement throughout the product lifecycle.
            </p>
          </motion.div>

          {/* Core Tools Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {coreTools.map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className={`h-full border-2 ${tool.borderColor} hover:shadow-xl transition-all duration-300`}>
                  <CardContent className="p-6">
                    {/* Icon and Title */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${tool.gradient} shadow-lg`}>
                        <tool.icon className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{tool.title}</h3>
                        <p className={`text-sm font-semibold ${tool.textColor}`}>{tool.fullName}</p>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {tool.description}
                    </p>

                    {/* Key Points */}
                    <div className="space-y-2">
                      {tool.keyPoints.map((point, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle className={`h-5 w-5 ${tool.textColor} flex-shrink-0 mt-0.5`} />
                          <span className="text-sm text-gray-700">{point}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-lg text-gray-700 font-medium"
          >
            Together, these tools ensure that products meet customer requirements from concept to production and beyond.
          </motion.p>
        </div>
      </section>

      {/* Why Core Tools Training Is Critical Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Core Tools Training Is Critical for Organizations?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {criticalReasons.map((reason, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-slate-600 to-gray-700 shadow-lg">
                        <reason.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">{reason.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{reason.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Attend Section */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Should Attend Core Tools Training?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {whoShouldAttend.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-white rounded-lg p-4 shadow-md border-l-4 border-slate-600 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-slate-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-800">{role}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Core Tools Training Benefits
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {trainingBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-t-4 border-slate-600">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-4 rounded-full bg-gradient-to-br from-slate-100 to-gray-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-slate-700" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iatf-16949" slugs={['iatf-16949', 'iso-9001', 'iso-14001']} heading="Related ISO Training Courses" />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-slate-700 via-gray-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Master Quality Core Tools?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join hundreds of quality professionals who have enhanced their skills and advanced their careers with our comprehensive Core Tools Training
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white text-slate-800 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
              >
                Enroll Now
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-800 font-semibold px-8 py-6 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Trainings Section */}
      <UpcomingTrainingsSection
        pageSlug="core-tools-training"
        title="Upcoming Core Tools Training Sessions"
      />
    </div>
  );
};
